import diagnostics from "@/services/diagnostics";
import { computed, ref, shallowRef, watch } from "vue";
import realtime from "@/services/realtime";
import { getPostRoundReplay } from "@/services/api";
import { createRoundSounds } from "@/services/roundSounds";
import { postRoundStageAt, remainingSeconds, rematchElapsed, replayFrameAt, replayElapsedAt, validReplay, validReplayRegion, POST_ROUND_NOTICES } from "@/services/postRound";

/** UI только отображает серверные сроки. Решение о реванше принимает bunker. */
export function usePostRound({ uuid, game, session, onResult, onRematch, audioConnected = () => true }) {
  const snapshot = shallowRef(null);
  const clock = ref(Date.now());
  const replayFrameUrl = ref("");
  const replayRegion = shallowRef(null);
  const replayKeyFrameOnly = ref(false);
  const replayUnavailable = ref(false);
  const rematchBusy = ref(false);
  const postRoundNotice = ref("");
  const sounds = createRoundSounds(() => session.notificationsEnabled && realtime.status.value === "open" && audioConnected());
  const stopAudioWatch = watch(() => [realtime.status.value, audioConnected()], () => sounds.refresh(), { flush: "sync" });
  let offset = 0;
  let ticker = null;
  let stopped = false;
  let replayRequested = false;
  let replay = null;
  let abortController = null;
  let fallbackImage = null;
  let fallbackTimer = null;
  let fallbackRequested = false;
  let fallbackUrl = "";
  let loadTimer = null;
  let busyTimer = null;
  let missingTimer = null;
  let lastStage = "";
  let deadlineSyncAt = 0;
  const played = new Set();
  const releaseDiagnostics = diagnostics.registerSource("postround", () => ({
    running: !stopped && ticker !== null, frames: replay?.frames?.length || 0,
    timers: [loadTimer, busyTimer, missingTimer, fallbackTimer].filter(id => id !== null).length,
    intervals: Number(ticker !== null), pending: !!abortController && !abortController.signal.aborted && !replay,
  }));

  const postRoundStage = computed(() => postRoundStageAt(snapshot.value, clock.value));
  const postRoundSeconds = computed(() => snapshot.value ? remainingSeconds(snapshot.value.deadline, clock.value) : 0);
  const rematchPending = computed(() => snapshot.value?.phase === "REMATCH_PENDING");
  const rematchInitiatorId = computed(() => snapshot.value?.initiatorUserId ?? null);
  const rematchProgress = computed(() => rematchPending.value ? rematchElapsed(snapshot.value.deadline, clock.value) : 0);
  const loserName = computed(() => snapshot.value?.loserName || game.result?.loserName || "Игрок");
  const lossReason = computed(() => (snapshot.value?.detectionType || game.mode) === "MOUTH" ? "Рот закрылся" : "Моргнул");
  const replayPlaying = computed(() => !!snapshot.value && snapshot.value.replayAvailable && !replayKeyFrameOnly.value && clock.value >= snapshot.value.revealAt &&
    clock.value < (snapshot.value.replayEndsAt ?? snapshot.value.clipEndsAt) && !replayUnavailable.value);
  const replayZoomProgress = computed(() => snapshot.value ? Math.min(1, Math.max(0,
    (clock.value - snapshot.value.revealAt) / Math.max(1, (snapshot.value.replayEndsAt ?? snapshot.value.clipEndsAt) - snapshot.value.revealAt))) : 0);
  const replayHolding = computed(() => postRoundStage.value === "hold");
  const postRoundActionsEnabled = computed(() => !!snapshot.value &&
    ["REVEAL", "REMATCH_PENDING"].includes(snapshot.value.phase) &&
    clock.value >= snapshot.value.clipEndsAt && clock.value < snapshot.value.deadline &&
    !replayPlaying.value && !rematchBusy.value && realtime.status.value === "open");

  function unavailable() {
    replayUnavailable.value = true;
    replayFrameUrl.value = "";
    replay = null;
    clearTimeout(loadTimer); loadTimer = null;
    if (abortController) abortController.abort();
    showFallback();
  }

  function showFallback() {
    if (!fallbackUrl || stopped) return;
    replayKeyFrameOnly.value = true;
    replayUnavailable.value = false;
    replayRegion.value = null;
    replayFrameUrl.value = fallbackUrl;
  }

  function loadFallback() {
    if (fallbackRequested || stopped) return;
    fallbackRequested = true;
    const url = game.result?.looserImgLink || game.looserImgLink;
    if (typeof url !== "string" || !url) return;
    fallbackImage = new Image();
    const clear = () => {
      clearTimeout(fallbackTimer); fallbackTimer = null;
      if (fallbackImage) { fallbackImage.onload = null; fallbackImage.onerror = null; }
    };
    fallbackTimer = setTimeout(clear, 2800);
    fallbackImage.onerror = clear;
    fallbackImage.onload = () => {
      clear();
      if (stopped) return;
      fallbackUrl = url;
      if (replayUnavailable.value) showFallback();
    };
    fallbackImage.src = url;
  }

  function onReplayImageError() {
    if (replayKeyFrameOnly.value) fallbackUrl = "";
    unavailable();
  }

  async function loadReplay() {
    if (replayRequested || stopped) return;
    replayRequested = true;
    if (!snapshot.value.replayAvailable) {
      unavailable();
      return;
    }
    abortController = new AbortController();
    // Запрос и декодирование ограничены по времени. При поздней синхронизации показываем тот же момент записи.
    const budget = Math.max(100, Math.min(2800, snapshot.value.deadline - clock.value));
    loadTimer = setTimeout(unavailable, budget);
    try {
      const value = await getPostRoundReplay(uuid, abortController.signal);
      if (!validReplay(value)) throw new Error("Недоступная запись");
      await Promise.all(value.frames.map((frame) => new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = resolve;
        image.onerror = reject;
        image.src = frame.image;
      })));
      if (stopped || replayUnavailable.value) return;
      clearTimeout(loadTimer); loadTimer = null;
      replay = value;
      replayKeyFrameOnly.value = false;
      const region = value.regions?.[(snapshot.value.detectionType || game.mode) === "MOUTH" ? "mouth" : "eyes"];
      replayRegion.value = validReplayRegion(region) ? region : null;
      tick();
    } catch (error) {
      if (!stopped) unavailable();
    }
  }

  function tick() {
    if (stopped) return;
    clock.value = Date.now() + offset;
    const state = snapshot.value;
    if (!state) return;
    const stage = postRoundStage.value;
    if (stage !== lastStage) {
      lastStage = stage;
      if (stage !== "suspense") sounds.stop("suspense");
      const sound = ["chat", "hold"].includes(stage) ? "reveal" : stage;
      if (["suspense", "reveal"].includes(sound) && !played.has(sound)) {
        played.add(sound);
        sounds.stop();
        sounds.play(sound);
      }
    }
    if (replay && clock.value >= state.revealAt) {
      const elapsed = replayElapsedAt(replay, state, clock.value);
      const index = clock.value >= (state.replayEndsAt ?? state.clipEndsAt) ? replay.keyFrameIndex : replayFrameAt(replay.frames, elapsed);
      replayFrameUrl.value = replay.frames[index].image;
    }
    if (clock.value >= state.deadline && clock.value - deadlineSyncAt >= 1000) {
      deadlineSyncAt = clock.value;
      realtime.send({ type: "sync_post_round", gameUUID: uuid }, { queue: false });
    }
    // Не держим камеры бесконечно, если сервер/сеть недоступны после известного дедлайна.
    if (clock.value > state.deadline + 5000) {
      realtime.send({ type: "post_round_next", gameUUID: uuid }, { ttl: 2000 });
      complete("Связь завершена. Ответ сервера недоступен");
    }
  }

  function complete(notice = "") {
    if (stopped) return;
    postRoundNotice.value = notice;
    game.postRoundNotice = notice;
    stop();
    onResult();
  }

  function applySnapshot(value) {
    if (stopped || !value || value.gameUUID !== uuid || !Number.isFinite(value.serverNow)) return;
    const previous = snapshot.value;
    if (previous && (value.revision < previous.revision ||
      (value.revision === previous.revision && value.serverNow <= previous.serverNow))) return;
    const localNow = Date.now();
    // Повторный пакет не отматывает клип, отсчёт и сектор R назад.
    const estimatedNow = previous ? localNow + offset : value.serverNow;
    snapshot.value = value;
    clock.value = Math.max(value.serverNow, estimatedNow);
    offset = clock.value - localNow;
    clearTimeout(missingTimer);
    missingTimer = null;
    clearTimeout(busyTimer); busyTimer = null;
    rematchBusy.value = false;
    postRoundNotice.value = POST_ROUND_NOTICES[value.reason] || "";
    if (value.phase === "CLOSED") {
      complete(postRoundNotice.value);
      return;
    }
    if (value.phase === "REMATCH") {
      if (!value.newRound?.uuid || value.newRound.uuid === uuid) {
        complete("Реванш недоступен");
        return;
      }
      stop();
      onRematch(value.newRound);
      return;
    }
    if (!ticker) ticker = setInterval(tick, 33);
    loadFallback();
    loadReplay();
    tick();
  }

  function begin(value) {
    if (value) applySnapshot(value);
    else if (!stopped && !missingTimer) {
      realtime.send({ type: "sync_post_round", gameUUID: uuid }, { queue: false });
      missingTimer = setTimeout(() => complete("Послераундовая связь недоступна"), 3500);
    }
  }

  function sendAction(type) {
    if (!postRoundActionsEnabled.value || stopped) return;
    if (type === "rematch_offer" && rematchPending.value) return;
    const initiator = String(rematchInitiatorId.value) === String(session.userId);
    if (["rematch_accept", "rematch_decline"].includes(type) && (!rematchPending.value || initiator)) return;
    if (type === "rematch_cancel" && (!rematchPending.value || !initiator)) return;
    if (!realtime.send({ type, gameUUID: uuid }, { queue: false })) return;
    rematchBusy.value = true;
    // Потерянный ответ не создаёт повторное предложение: только синхронизация состояния.
    busyTimer = setTimeout(() => {
      busyTimer = null;
      if (stopped) return;
      rematchBusy.value = false;
      realtime.send({ type: "sync_post_round", gameUUID: uuid }, { queue: false });
    }, 2000);
  }

  function stop() {
    if (stopped) return;
    stopped = true;
    releaseDiagnostics();
    clearInterval(ticker);
    clearTimeout(loadTimer); loadTimer = null;
    clearTimeout(busyTimer); busyTimer = null;
    clearTimeout(missingTimer); missingTimer = null;
    clearTimeout(fallbackTimer); fallbackTimer = null;
    if (fallbackImage) { fallbackImage.onload = null; fallbackImage.onerror = null; fallbackImage = null; }
    if (abortController) abortController.abort();
    replay = null;
    stopAudioWatch();
    sounds.dispose();
  }

  return {
    postRoundStage, postRoundSeconds, replayFrameUrl, replayPlaying, replayHolding, replayZoomProgress, replayRegion, replayKeyFrameOnly, replayUnavailable,
    loserName, lossReason, rematchInitiatorId, rematchProgress, rematchPending, rematchBusy,
    postRoundNotice, postRoundActionsEnabled,
    offerRematch: () => sendAction("rematch_offer"),
    acceptRematch: () => sendAction("rematch_accept"),
    declineRematch: () => sendAction("rematch_decline"),
    cancelRematch: () => sendAction("rematch_cancel"),
    finishPostRound: () => sendAction("post_round_next"),
    onReplayImageError, begin, applySnapshot, stop,
  };
}
